import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { signIn, signOut, signUp, confirmSignUp, getCurrentUser, AuthUser, fetchUserAttributes, resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { 
  UserRole, 
  SubscriptionTier, 
  parseCustomAttributes, 
  formatCustomAttributes, 
  getErrorMessage,
  DEFAULT_USER_ATTRIBUTES 
} from '../config/cognito';

interface CognitoUser {
  username: string;
  email: string;
  email_verified: boolean;
  sub: string;
  name?: string;
  role: UserRole;
  subscriptionTier: SubscriptionTier;
  onboardingCompleted: boolean;
}

interface AuthContextType {
  // Authentication state
  isAuthenticated: boolean;
  user: CognitoUser | null;
  loading: boolean;
  error: string | null;
  
  // Authentication actions
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  signUp: (email: string, password: string, name: string, inviteCode: string, role?: UserRole) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  
  // Password reset actions
  resetPassword: (email: string) => Promise<void>;
  confirmResetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  
  // User management
  updateUserAttributes: (attributes: Partial<CognitoUser>) => Promise<void>;
  refreshUser: () => Promise<void>;
  
  // Error handling
  clearError: () => void;
  
  // Utility functions
  hasRole: (role: UserRole) => boolean;
  isSubscriptionTier: (tier: SubscriptionTier) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<CognitoUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user is already authenticated on app load
  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      setLoading(true);
      
      // Check for authenticated user with Cognito
      const currentUser = await getCurrentUser();
      
      if (currentUser) {
        // Fetch user attributes to get custom attributes
        const attributes = await fetchUserAttributes();
        const customAttrs = parseCustomAttributes(attributes);
        
        const userData: CognitoUser = {
          username: currentUser.username,
          email: attributes.email || currentUser.signInDetails?.loginId || '',
          email_verified: attributes.email_verified === 'true',
          sub: currentUser.userId,
          name: `${attributes.given_name || ''} ${attributes.family_name || ''}`.trim() || attributes.name,
          role: customAttrs.role,
          subscriptionTier: customAttrs.subscriptionTier,
          onboardingCompleted: customAttrs.onboardingCompleted,
        };
        
        setUser(userData);
        setIsAuthenticated(true);
      }
    } catch (error) {
      // User is not authenticated
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      
      // Cognito authentication
      const result = await signIn({ 
        username: email, 
        password 
      });
      
      // Check if user needs to confirm their email
      if (!result.isSignedIn && result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        try {
          // Import resendSignUpCode dynamically to avoid circular dependency
          const { resendSignUpCode } = await import('aws-amplify/auth');
          await resendSignUpCode({ username: email });
          
          // Store email for verification page
          sessionStorage.setItem('pendingVerificationEmail', email);
          
          // Don't set error in context - let LoginPage handle the UI
          // Throw a special error that the LoginPage can catch
          const verificationError = new Error('VERIFICATION_REQUIRED');
          (verificationError as any).email = email;
          throw verificationError;
        } catch (resendError: any) {
          // If resend fails, still redirect but show the original error
          if (resendError.message === 'VERIFICATION_REQUIRED') {
            throw resendError;
          }
          // If resend code failed, set error and throw
          setError('Please verify your email address. Check your inbox for the verification code.');
          throw new Error('VERIFICATION_REQUIRED');
        }
      }
      
      if (result.isSignedIn) {
        await checkAuthState();
      }
    } catch (error: any) {
      // If it's our custom VERIFICATION_REQUIRED error, re-throw it
      if (error?.message === 'VERIFICATION_REQUIRED') {
        throw error;
      }
      
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Cognito sign out
      await signOut();
      setIsAuthenticated(false);
      setUser(null);
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (email: string, password: string, name: string, inviteCode: string, role: UserRole = 'creator') => {
    try {
      setLoading(true);
      setError(null);
      
      // Split name into first and last name for Cognito requirements
      const nameParts = name.trim().split(' ');
      const givenName = nameParts[0] || '';
      const familyName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : nameParts[0] || 'User';
      
      // Format custom attributes for signup
      const customAttributes = formatCustomAttributes({
        role,
        subscriptionTier: DEFAULT_USER_ATTRIBUTES.subscriptionTier,
        onboardingCompleted: false,
      });
      
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            given_name: givenName,
            family_name: familyName,
            ...customAttributes,
            'custom:inviteCode': inviteCode, // Must match the custom attribute name defined in Cognito User Pool
          },
        },
      });
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignUp = async (email: string, code: string) => {
    try {
      setLoading(true);
      setError(null);
      
      await confirmSignUp({ 
        username: email, 
        confirmationCode: code 
      });
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (email: string) => {
    try {
      setLoading(true);
      setError(null);
      
      await resetPassword({ username: email });
      // Cognito sends verification code to user's email
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmResetPassword = async (email: string, code: string, newPassword: string) => {
    try {
      setLoading(true);
      setError(null);
      
      await confirmResetPassword({ 
        username: email, 
        confirmationCode: code,
        newPassword 
      });
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const updateUserAttributes = async (attributes: Partial<CognitoUser>) => {
    try {
      setLoading(true);
      setError(null);
      
      // This would typically use updateUserAttributes from Amplify
      // For now, we'll update the local state
      if (user) {
        const updatedUser = { ...user, ...attributes };
        setUser(updatedUser);
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    await checkAuthState();
  };

  const clearError = () => {
    setError(null);
  };

  const hasRole = (role: UserRole): boolean => {
    return user?.role === role;
  };

  const isSubscriptionTier = (tier: SubscriptionTier): boolean => {
    return user?.subscriptionTier === tier;
  };

  const value: AuthContextType = {
    isAuthenticated,
    user,
    loading,
    error,
    signIn: handleSignIn,
    signOut: handleSignOut,
    signUp: handleSignUp,
    confirmSignUp: handleConfirmSignUp,
    resetPassword: handleResetPassword,
    confirmResetPassword: handleConfirmResetPassword,
    updateUserAttributes,
    refreshUser,
    clearError,
    hasRole,
    isSubscriptionTier,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Additional custom hooks for specific authentication scenarios

export function useAuthGuard() {
  const { isAuthenticated, loading } = useAuth();
  
  return {
    isAuthenticated,
    loading,
    canAccess: isAuthenticated && !loading,
  };
}

export function useUserRole() {
  const { user, hasRole } = useAuth();
  
  return {
    role: user?.role || 'creator',
    isCreator: hasRole('creator'),
    isAdmin: hasRole('admin'),
    hasRole,
  };
}

export function useSubscription() {
  const { user, isSubscriptionTier } = useAuth();
  
  return {
    tier: user?.subscriptionTier || 'free',
    isFree: isSubscriptionTier('free'),
    isPro: isSubscriptionTier('pro'),
    isEnterprise: isSubscriptionTier('enterprise'),
    isSubscriptionTier,
  };
}